import { z } from "zod";
import { sumBy } from "es-toolkit";
import {
  DaysOfTheWeekSchema,
  DateRecordSchema,
  DateTimeRecordSchema,
  TimeZoneSchema,
  EffectiveTimeRangesSchema,
  compareDateTimeRecords,
} from "./date_time";

export type FeatureName = string;
const FeatureNameSchema = z.string().min(1);

export type FeatureState = string;
const FeatureStateSchema = z.string().min(1);

export const Feature = <TFeatureState extends string>(featureState: z.ZodType<TFeatureState, TFeatureState>) =>
  z
    .object({
      default_state: featureState,
      allowed_states: z.array(featureState).min(1),
    })
    .refine((feature) => feature.allowed_states.includes(feature.default_state), {
      message: "Feature default_state must be included in allowed_states",
      path: ["default_state"],
    })
    .brand("Feature");

export const FeatureSchema = Feature(FeatureStateSchema);

/**
 * A feature definition with typed states.
 *
 * @template TFeatureState - The type of feature states
 */
export type Feature<TFeatureState extends string = string> = z.infer<ReturnType<typeof Feature<TFeatureState>>>;

/**
 * @deprecated
 * Use FeatureSchema.decode() or FeatureSchema.parse() depending on your use case
 */
export function makeFeature(data: Partial<Feature>) {
  return FeatureSchema.parse(data);
}

/**
 * A record mapping feature names to their definitions.
 *
 * @template TFeatureName - The type of feature names
 * @template TFeatureState - The type of feature states
 */
export const Features = <TFeatureName extends string, TFeatureState extends string>(
  featureNameSchema: z.ZodType<TFeatureName, TFeatureName>,
  featureStateSchema: z.ZodType<TFeatureState, TFeatureState>,
) => z.record(featureNameSchema, Feature(featureStateSchema));

export const FeaturesSchema = Features(FeatureNameSchema, FeatureStateSchema);
export type Features<TFeatureName extends string = string, TFeatureState extends string = string> = z.infer<
  ReturnType<typeof Features<TFeatureName, TFeatureState>>
>;

/**
 * A partial record mapping feature names to states, used in rules.
 * Rules don't need to specify all features, only the ones they affect.
 *
 * @template TFeatureName - The type of feature names
 * @template TFeatureState - The type of feature states
 */
export const FeaturesState = <TFeatureName extends string, TFeatureState extends string>(
  featureNameSchema: z.ZodType<TFeatureName, TFeatureName>,
  featureStateSchema: z.ZodType<TFeatureState, TFeatureState>,
) => z.partialRecord(featureNameSchema, featureStateSchema);

export type FeaturesState<TFeatureName extends string = string, TFeatureState extends string = string> = z.infer<
  ReturnType<typeof FeaturesState<TFeatureName, TFeatureState>>
>;

export const OptionalEffectivePeriodSchema = z
  .object({
    start: DateTimeRecordSchema.nullable(),
    end: DateTimeRecordSchema.nullable(),
  })
  .refine(
    (period) => {
      if (period.start !== null && period.end !== null) {
        return compareDateTimeRecords(period.start, period.end) < 0;
      }
      return true;
    },
    {
      message: "Effective period start must be before end",
      path: ["start"],
    },
  );
export type OptionalEffectivePeriod = z.infer<typeof OptionalEffectivePeriodSchema>;

export const RequiredEffectivePeriodSchema = z
  .object({
    start: DateTimeRecordSchema,
    end: DateTimeRecordSchema,
  })
  .refine((period) => compareDateTimeRecords(period.start, period.end) < 0, {
    message: "Effective period start must be before end",
    path: ["start"],
  });
export type RequiredEffectivePeriod = z.infer<typeof RequiredEffectivePeriodSchema>;

export const AbsoluteRangeEffectivePeriodSchema = z
  .object({
    start: DateTimeRecordSchema,
    end: DateTimeRecordSchema.nullable(),
  })
  .refine(
    (period) => {
      if (period.end !== null) {
        return compareDateTimeRecords(period.start, period.end) < 0;
      }
      return true;
    },
    {
      message: "Effective period start must be before end",
      path: ["start"],
    },
  );
export type AbsoluteRangeEffectivePeriod = z.infer<typeof AbsoluteRangeEffectivePeriodSchema>;

const BaseRule = <TFeatureName extends string, TFeatureState extends string>(
  featureNameSchema: z.ZodType<TFeatureName, TFeatureName>,
  featureStateSchema: z.ZodType<TFeatureState, TFeatureState>,
) =>
  z.object({
    id: z.string(),
    name: z.string().max(100),
    features: FeaturesState(featureNameSchema, featureStateSchema),
  });

const WeeklyPatternSchema = z.object({
  effective_period: OptionalEffectivePeriodSchema,
  days: DaysOfTheWeekSchema,
  time_ranges: EffectiveTimeRangesSchema,
});
export type WeeklyPattern = z.infer<typeof WeeklyPatternSchema>;

/**
 * Weekly pattern rule with optional feature name/state typing.
 *
 * @template TFeatureName - The type of feature names
 * @template TFeatureState - The type of feature states
 */
export const WeeklyPatternRule = <TFeatureName extends string, TFeatureState extends string>(
  featureNameSchema: z.ZodType<TFeatureName, TFeatureName>,
  featureStateSchema: z.ZodType<TFeatureState, TFeatureState>,
) =>
  BaseRule(featureNameSchema, featureStateSchema).safeExtend({
    kind: z.literal("weekly_pattern"),
    weekly_pattern: WeeklyPatternSchema,
  });

export const WeeklyPatternRuleSchema = WeeklyPatternRule(FeatureNameSchema, FeatureStateSchema);

export type WeeklyPatternRule<TFeatureName extends string = string, TFeatureState extends string = string> = z.infer<
  ReturnType<typeof WeeklyPatternRule<TFeatureName, TFeatureState>>
>;

/**
 * @deprecated
 * Use WeeklyPatternRuleSchema.decode() or WeeklyPatternRuleSchema.parse() depending on your use case
 */
export function makeWeeklyPatternRule(data: Partial<WeeklyPatternRule>) {
  return WeeklyPatternRuleSchema.parse({
    ...data,
    kind: "weekly_pattern",
  });
}

export const ROTATING_PATTERN_MIN_DURATION_IN_DAYS = 1;
export const ROTATING_PATTERN_INTERVAL_MIN_DURATION_IN_DAYS = 1;
// Rotating patterns are expected to be used for sites with rotating shifts where
// the schedule is likely to span weeks or months at most. To help catch programmatic
// errors, we set limits on durations. If legitimate usecases run up against these
// arbitrary limits, we should change or remove them.
export const ROTATING_PATTERN_MAX_DURATION_IN_DAYS = 365 * 3;
export const ROTATING_PATTERN_INTERVAL_MAX_DURATION_IN_DAYS = 365;

const BaseRotatingPatternIntervalSchema = z.object({
  days: z
    .number()
    .int()
    .min(ROTATING_PATTERN_INTERVAL_MIN_DURATION_IN_DAYS)
    .max(ROTATING_PATTERN_INTERVAL_MAX_DURATION_IN_DAYS),
});

const RotatingPatternWithTimeRangesSchema = BaseRotatingPatternIntervalSchema.extend({
  time_ranges: EffectiveTimeRangesSchema,
}).strict();

const RotatingPatternWithOffDaySchema = BaseRotatingPatternIntervalSchema.extend({
  is_off_day: z.literal(true),
}).strict();

export const RotatingPatternSchema = z.union([RotatingPatternWithTimeRangesSchema, RotatingPatternWithOffDaySchema]);
export type RotatingPatternInterval = z.infer<typeof RotatingPatternSchema>;

export const RotatingPatternDefinitionSchema = z.object({
  effective_period: OptionalEffectivePeriodSchema,
  start: DateRecordSchema,
  pattern: z.array(RotatingPatternSchema).min(1),
});
export type RotatingPattern = z.infer<typeof RotatingPatternDefinitionSchema>;

const RotatingPatternRuleUnbranded = <TFeatureName extends string, TFeatureState extends string>(
  featureNameSchema: z.ZodType<TFeatureName, TFeatureName>,
  featureStateSchema: z.ZodType<TFeatureState, TFeatureState>,
) =>
  BaseRule(featureNameSchema, featureStateSchema)
    .safeExtend({
      kind: z.literal("rotating_pattern"),
      rotating_pattern: RotatingPatternDefinitionSchema,
    })
    .refine(
      (rule) => {
        const patternDuration = sumBy(rule.rotating_pattern.pattern, (interval) => interval.days);
        return (
          patternDuration >= ROTATING_PATTERN_MIN_DURATION_IN_DAYS &&
          patternDuration <= ROTATING_PATTERN_MAX_DURATION_IN_DAYS
        );
      },
      {
        message: `Pattern total duration must be between ${ROTATING_PATTERN_MIN_DURATION_IN_DAYS.toString()} and ${ROTATING_PATTERN_MAX_DURATION_IN_DAYS.toString()} days`,
        path: ["rotating_pattern", "pattern"],
      },
    );

export const RotatingPatternRule = <TFeatureName extends string, TFeatureState extends string>(
  featureNameSchema: z.ZodType<TFeatureName, TFeatureName>,
  featureStateSchema: z.ZodType<TFeatureState, TFeatureState>,
) => RotatingPatternRuleUnbranded(featureNameSchema, featureStateSchema).brand("RotatingPatternRule");

export const RotatingPatternRuleSchema = RotatingPatternRule(FeatureNameSchema, FeatureStateSchema);

/**
 * Rotating pattern rule with optional feature name/state typing.
 *
 * @template TFeatureName - The type of feature names
 * @template TFeatureState - The type of feature states
 */
export type RotatingPatternRule<TFeatureName extends string = string, TFeatureState extends string = string> = z.infer<
  ReturnType<typeof RotatingPatternRule<TFeatureName, TFeatureState>>
>;

/**
 * @deprecated
 * Use RotatingPatternRuleSchema.decode() or RotatingPatternRuleSchema.parse() depending on your use case
 */
export function makeRotatingPatternRule(data: Partial<RotatingPatternRule>) {
  return RotatingPatternRuleSchema.parse({
    ...data,
    kind: "rotating_pattern",
  });
}

const AbsoluteRangeSchema = z.object({
  effective_period: AbsoluteRangeEffectivePeriodSchema,
});
export type AbsoluteRange = z.infer<typeof AbsoluteRangeSchema>;

export const AbsoluteRangeRule = <TFeatureName extends string, TFeatureState extends string>(
  featureNameSchema: z.ZodType<TFeatureName, TFeatureName>,
  featureStateSchema: z.ZodType<TFeatureState, TFeatureState>,
) =>
  BaseRule(featureNameSchema, featureStateSchema).safeExtend({
    kind: z.literal("absolute_range"),
    absolute_range: AbsoluteRangeSchema,
  });

export const AbsoluteRangeRuleSchema = AbsoluteRangeRule(FeatureNameSchema, FeatureStateSchema);

/**
 * Absolute range rule with optional feature name/state typing.
 *
 * @template TFeatureName - The type of feature names
 * @template TFeatureState - The type of feature states
 */
export type AbsoluteRangeRule<TFeatureName extends string = string, TFeatureState extends string = string> = z.infer<
  ReturnType<typeof AbsoluteRangeRule<TFeatureName, TFeatureState>>
>;

/**
 * @deprecated
 * Use AbsoluteRangeRuleSchema.decode() or AbsoluteRangeRuleSchema.parse() depending on your use case
 */
export function makeAbsoluteRangeRule(data: Partial<AbsoluteRangeRule>) {
  return AbsoluteRangeRuleSchema.parse({
    ...data,
    kind: "absolute_range",
  });
}

export const Rule = <TFeatureName extends string, TFeatureState extends string>(
  featureNameSchema: z.ZodType<TFeatureName, TFeatureName>,
  featureStateSchema: z.ZodType<TFeatureState, TFeatureState>,
) =>
  z.discriminatedUnion("kind", [
    WeeklyPatternRule(featureNameSchema, featureStateSchema),
    RotatingPatternRule(featureNameSchema, featureStateSchema),
    AbsoluteRangeRule(featureNameSchema, featureStateSchema),
  ]);

export const RuleSchema = Rule(FeatureNameSchema, FeatureStateSchema);

/**
 * Union of all rule types.
 *
 * @template TFeatureName - The type of feature names
 * @template TFeatureState - The type of feature states
 */
export type Rule<TFeatureName extends string = string, TFeatureState extends string = string> = z.infer<
  ReturnType<typeof Rule<TFeatureName, TFeatureState>>
>;

export const PartialRuleSchema = z.discriminatedUnion("kind", [
  WeeklyPatternRuleSchema.partial().required({ id: true, kind: true }),
  z.object(RotatingPatternRuleSchema.shape).partial().required({ id: true, kind: true }),
  AbsoluteRangeRuleSchema.partial().required({ id: true, kind: true }),
]);

export const isWeeklyPatternRule = <TFeatureName extends string = string, TFeatureState extends string = string>(
  rule: Rule<TFeatureName, TFeatureState>,
): rule is WeeklyPatternRule<TFeatureName, TFeatureState> => {
  return rule.kind === "weekly_pattern";
};

export const isRotatingPatternRule = <TFeatureName extends string = string, TFeatureState extends string = string>(
  rule: Rule<TFeatureName, TFeatureState>,
): rule is RotatingPatternRule<TFeatureName, TFeatureState> => {
  return rule.kind === "rotating_pattern";
};

export const isAbsoluteRangeRule = <TFeatureName extends string = string, TFeatureState extends string = string>(
  rule: Rule<TFeatureName, TFeatureState>,
): rule is AbsoluteRangeRule<TFeatureName, TFeatureState> => {
  return rule.kind === "absolute_range";
};

const ScheduleMetadataSchema = z.object({
  updated_at_utc: z.iso.datetime(),
  suggested_input_time_precision_in_minutes: z.literal(10),
  expected_time_precision_in_minutes: z.number().int().min(1).max(60),
  timezone: TimeZoneSchema,
});
export type ScheduleMetadata = z.infer<typeof ScheduleMetadataSchema>;

const ScheduleShape = <TFeatureName extends string, TFeatureState extends string>(
  featureNameSchema: z.ZodType<TFeatureName, TFeatureName>,
  featureStateSchema: z.ZodType<TFeatureState, TFeatureState>,
) =>
  z
    .object({
      schema_version: z.literal("1"),
      metadata: ScheduleMetadataSchema,
      features: Features(featureNameSchema, featureStateSchema),
      rules: z.array(Rule(featureNameSchema, featureStateSchema)),
    })
    .refine(
      (schedule) => {
        const featureIds = Object.keys(schedule.features);
        for (const rule of schedule.rules) {
          for (const featureId of Object.keys(rule.features)) {
            if (!featureIds.includes(featureId)) {
              return false;
            }
          }
        }
        return true;
      },
      {
        message: "Rule features must reference existing feature IDs",
        path: ["rules"],
      },
    )
    .refine(
      (schedule) => {
        for (const rule of schedule.rules) {
          for (const [featureId, state] of Object.entries(rule.features)) {
            const feature = schedule.features[featureId as TFeatureName];
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- feature may be undefined if TFeatureName is an open type e.g string
            if (feature && !feature.allowed_states.includes(state as TFeatureState)) {
              return false;
            }
          }
        }
        return true;
      },
      {
        message: "Rule feature states must be included in feature's allowed_states",
        path: ["rules"],
      },
    )
    .refine(
      (schedule) => {
        const ruleIds: Set<string> = new Set();
        for (const rule of schedule.rules) {
          if (ruleIds.has(rule.id)) {
            return false;
          }
          ruleIds.add(rule.id);
        }
        return true;
      },
      {
        message: "Rule ids must be unique",
        path: ["rules"],
      },
    );

export const Schedule = <TFeatureName extends string, TFeatureState extends string>(
  featureNameSchema: z.ZodType<TFeatureName, TFeatureName>,
  featureStateSchema: z.ZodType<TFeatureState, TFeatureState>,
) => ScheduleShape(featureNameSchema, featureStateSchema).brand("Schedule");

export const ScheduleSchema = Schedule(FeatureNameSchema, FeatureStateSchema);

/**
 * A schedule defining features and rules for state evaluation.
 *
 * When using default types (string), this is exactly the Zod-inferred type.
 * When using specific types, features and rules are narrowed.
 *
 * @template TFeatureName - The type of feature names
 * @template TFeatureState - The type of feature states
 */
export type Schedule<TFeatureName extends string = string, TFeatureState extends string = string> = z.infer<
  ReturnType<typeof Schedule<TFeatureName, TFeatureState>>
>;

/**
 * @deprecated
 * Use ScheduleSchema.decode() or ScheduleSchema.parse() depending on your use case
 */
export function makeSchedule(data: unknown) {
  return ScheduleSchema.parse(data);
}
